<template>
  <div class="benefits-page">
    <main>
      <section class="benefits-hero" aria-labelledby="benefits-hero-title">
        <v-container class="benefits-container benefits-hero__content">
          <p class="benefits-eyebrow">Reckon It All</p>
          <h1 id="benefits-hero-title" class="benefits-title benefits-title--hero">
            Why Join Reckon?
          </h1>
          <p class="benefits-hero__subhead">
            Do great work. Keep growing. Enjoy the adventure.
          </p>
          <div class="benefits-copy benefits-copy--lead">
            <p>We built Reckon to be the kind of company talented people want to be part of.</p>
            <p>
              A place where you're trusted to do great work, encouraged to keep learning,
              surrounded by people who want to see you succeed, and given opportunities to
              experience a little more along the way.
            </p>
            <p>Because a career should be more than a job.</p>
            <p>It should be a story worth telling.</p>
          </div>
          <div class="benefits-actions">
            <v-btn large color="red" :to="{ name: 'contact' }">Join the Crew</v-btn>
            <v-btn large outlined :to="{ name: 'reckon-way-people' }">Meet the Heroes</v-btn>
          </div>
        </v-container>
      </section>

      <section class="benefits-section benefits-section--light" aria-labelledby="super-power-title">
        <v-container class="benefits-container benefits-narrative benefits-narrative--wide">
          <div>
            <h2 id="super-power-title" class="benefits-title">Find Your Super Power</h2>
            <div class="benefits-copy">
              <p>You don't have to fit a mold to belong at Reckon.</p>
              <p>
                Our Crew brings together different backgrounds, perspectives, personalities, and
                technical strengths. Some people build. Some architect. Some test, organize,
                automate, analyze, teach, or see the thing everyone else missed.
              </p>
              <p>
                We want you to bring what you're great at and have the opportunity to discover
                what you're capable of next.
              </p>
            </div>
            <div class="benefits-actions">
              <v-btn large outlined :to="{ name: 'reckon-way-people' }">Meet the Heroes</v-btn>
            </div>
          </div>
          <figure class="benefits-photo-card">
            <v-img
              :src="crewImage | mediaSrc"
              class="benefits-photo-card__image"
              contain
              role="img"
              aria-label="The Reckon Crew together."
            />
            <figcaption>Different strengths. One Crew.</figcaption>
          </figure>
        </v-container>
      </section>

      <section class="benefits-section benefits-section--dark" aria-labelledby="growth-title">
        <v-container class="benefits-container benefits-narrative">
          <div>
            <p class="benefits-eyebrow">Growth</p>
            <h2 id="growth-title" class="benefits-title">Never Stop Leveling Up</h2>
            <p class="benefits-section__subhead">Curiosity Comes With the Job</p>
          </div>
          <div class="benefits-copy">
            <p>Technology doesn't stand still, and neither should your career.</p>
            <p>
              We invest in opportunities to learn new technologies, deepen your expertise, attend
              industry conferences, learn from teammates, and take on challenges that stretch what
              you thought you could do.
            </p>
            <p>Growth at Reckon isn't about checking boxes on a career ladder.</p>
            <p>It's about becoming better at what you do and discovering where you want to go next.</p>
          </div>
        </v-container>
      </section>

      <section class="benefits-section benefits-section--adventure" aria-labelledby="life-at-reckon-growth-title">
        <v-container class="benefits-container benefits-narrative benefits-narrative--wide">
          <div>
            <p class="benefits-eyebrow">Life at Reckon</p>
            <h2 id="life-at-reckon-growth-title" class="benefits-title">
              Some of the Best Growth Happens Away From a Desk
            </h2>
            <div class="benefits-copy">
              <p>Conferences. Team dinners. Traditions. Adventures. New places. Unexpected conversations.</p>
              <p>
                We believe shared experiences build stronger relationships, and stronger
                relationships build better teams.
              </p>
              <p>
                That's why getting out into the world together isn't separate from how we build
                Reckon.
              </p>
              <p>It's part of it.</p>
            </div>
            <div class="benefits-actions">
              <v-btn large outlined :to="{ name: 'reckon-way' }">Explore The Reckon Way</v-btn>
            </div>
          </div>
          <v-img
            :src="adventureImage | mediaSrc"
            aspect-ratio="1.2"
            class="benefits-feature-image"
            role="img"
            aria-label="The Reckon Crew on a shared outdoor adventure."
          />
        </v-container>
      </section>

      <section class="benefits-section benefits-section--light" aria-labelledby="person-title">
        <v-container class="benefits-container benefits-narrative">
          <h2 id="person-title" class="benefits-title">You're a Person Here, Not a Resource</h2>
          <div class="benefits-copy">
            <p>We want to know the people we work with.</p>
            <p>
              What you're interested in. What you want to learn. Where you want your career to go.
              What you're great at today and what you'd like to be great at tomorrow.
            </p>
            <p>
              As Reckon grows, we want to preserve a company where people have a voice, ideas can
              come from anywhere, and everyone has the opportunity to make an impact.
            </p>
          </div>
        </v-container>
      </section>

      <section class="benefits-section benefits-section--benefits" aria-labelledby="benefit-details-title">
        <v-container class="benefits-container">
          <div class="benefits-section__intro">
            <p class="benefits-eyebrow">Benefits</p>
            <h2 id="benefit-details-title" class="benefits-title">Yes, There Are Benefits Too.</h2>
            <div class="benefits-copy">
              <p>We believe taking care of our people means supporting life both inside and outside of work.</p>
              <p>
                Our benefits are designed to give the Crew the flexibility, security, and support
                to build a career and a life they're proud of.
              </p>
            </div>
          </div>

          <div class="benefit-grid">
            <article v-for="group in benefitGroups" :key="group.title" class="benefit-card">
              <p class="benefit-card__label">{{ group.title }}</p>
              <h3 class="benefit-card__title">{{ group.heroLabel }}</h3>
              <ul class="benefit-card__list">
                <li v-for="item in group.items" :key="item">{{ item }}</li>
              </ul>
            </article>
          </div>
          <!-- TODO: Verify/update annual federal holiday count before publishing. -->
        </v-container>
      </section>

      <section class="benefits-section benefits-section--life" aria-labelledby="life-at-reckon-title">
        <v-container class="benefits-container">
          <div class="benefits-section__intro benefits-section__intro--centered">
            <h2 id="life-at-reckon-title" class="benefits-title">Life at Reckon</h2>
            <div class="benefits-copy">
              <p>The benefits tell part of the story.</p>
              <p>The rest looks more like this.</p>
            </div>
          </div>
          <div class="life-gallery">
            <router-link
              v-for="story in lifeStories"
              :key="story.title"
              :to="story.href"
              class="life-gallery__item"
            >
              <v-img
                :src="story.src | mediaSrc"
                aspect-ratio="1.25"
                class="life-gallery__image"
                role="img"
                :aria-label="story.alt"
              />
              <span class="life-gallery__caption">
                <small>{{ story.category }}</small>
                <strong>{{ story.title }}</strong>
              </span>
            </router-link>
          </div>
          <p class="life-gallery__support">This is what The Reckon Way looks like.</p>
          <div class="benefits-actions benefits-actions--centered">
            <v-btn large outlined :to="{ name: 'reckon-way-stories' }">Explore the Stories</v-btn>
          </div>
        </v-container>
      </section>

      <section class="benefits-section benefits-section--closing" aria-labelledby="origin-story-title">
        <v-container class="benefits-container benefits-closing">
          <p class="benefits-eyebrow">Your Next Chapter</p>
          <h2 id="origin-story-title" class="benefits-title">Your Origin Story Could Start Here</h2>
          <div class="benefits-copy">
            <p>Every member of the Crew started somewhere.</p>
            <p>
              Maybe you're looking for a bigger technical challenge. Maybe you want room to grow.
              Maybe you're ready for a different kind of company.
            </p>
            <p>
              Whatever brought you here, we're always interested in meeting curious people who
              care about what they build and who they build it with.
            </p>
          </div>
          <div class="benefits-actions benefits-actions--centered">
            <v-btn large color="red" :to="{ name: 'contact' }">Explore Open Roles</v-btn>
            <v-btn large outlined :to="{ name: 'reckon-way-people' }">Meet the Heroes</v-btn>
          </div>
          <p class="benefits-closing__line">Capes optional. Curiosity required.</p>
        </v-container>
      </section>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import AppFooter from '../components/Footer.vue';
import { publishedNarratives } from '../content/culture/narratives';
import { mediaRef } from '../services/mediaAssets';

const lifeStoryTitles = [
  'Gate A24',
  'ATVs in the Mojave Desert',
  'UTVs in the Mojave Desert',
  'Zion National Park',
];

const lifeStories = lifeStoryTitles.map((title) => {
  const story = publishedNarratives.find((narrative) => narrative.title === title);
  return {
    title: story.title,
    category: story.category,
    src: story.image,
    alt: story.imageAlt,
    href: story.href,
  };
});

export default {
  name: 'BenefitsPage',
  components: { AppFooter },
  data: () => ({
    crewImage: mediaRef('reckon_crew.jpeg'),
    adventureImage: mediaRef('adventures/aws-reinvent-2024-zion-20241201-171229.jpg'),
    benefitGroups: [
      {
        title: 'Health & Wellness',
        heroLabel: 'Take care of your secret identity.',
        items: [
          '100% Employer-Paid Medical / Dental / Vision Benefits',
          'Anthem Platinum PPO ($0 deductible)',
          'Principal Dental',
          'Principal Vision',
        ],
      },
      {
        title: 'Retirement',
        heroLabel: 'Plan for the sequel.',
        items: [
          'Employer-Sponsored 401(k) Plan (Vanguard)',
          '12% Employer Contribution',
          'Immediately vesting at 100%',
          'Potential for up to 3% profit sharing',
        ],
      },
      {
        title: 'Time Off',
        heroLabel: 'Every hero needs to recharge.',
        items: [
          'All Federal Holidays Paid (11 most years)',
          'Flexible PTO plans to suit your needs',
        ],
      },
      {
        title: 'Employee Growth',
        heroLabel: 'Upgrade your powers.',
        items: ['Budget for Training or Trade Conference attendance, travel, and lodging'],
      },
      {
        title: 'Equity',
        heroLabel: 'Own part of the story.',
        items: ['All employees are eligible to participate in the Reckon Equity Plan'],
      },
      {
        title: 'Events',
        heroLabel: 'Adventure is part of the package.',
        items: [
          'Happy Hours and weekly AMAs',
          'Holiday Party',
          'Yearly Corporate Retreat',
        ],
      },
    ],
    lifeStories,
  }),
};
</script>

<style scoped>
.benefits-page {
  background: #080808;
  color: #fff;
}

.benefits-container {
  max-width: 1180px;
}

.benefits-hero,
.benefits-section {
  padding: 84px 0;
}

.benefits-hero {
  align-items: center;
  background: radial-gradient(circle at 82% 15%, #33110f 0, #090909 48%);
  display: flex;
  min-height: 0;
}

.benefits-hero__content {
  max-width: 1180px;
}

.benefits-eyebrow {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: .82rem;
  font-weight: 900;
  letter-spacing: .08em;
  margin: 0 0 12px;
  text-transform: uppercase;
}

.benefits-title {
  color: inherit;
  font-family: "Oswald", sans-serif;
  font-size: clamp(2.3rem, 5vw, 4rem);
  font-weight: 700;
  letter-spacing: -.02em;
  line-height: 1.05;
  margin: 0 0 24px;
  max-width: 880px;
}

.benefits-title--hero {
  font-size: clamp(2.5rem, 5vw, 4.8rem);
  line-height: 1.03;
  max-width: 880px;
}

.benefits-hero__subhead,
.benefits-section__subhead {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: clamp(1.35rem, 2.5vw, 2rem);
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 30px;
}

.benefits-copy {
  color: #bdbdbd;
  max-width: 720px;
  font-size: 1.08rem;
  line-height: 1.75;
}

.benefits-copy--lead {
  max-width: 760px;
  font-size: clamp(1.1rem, 2vw, 1.28rem);
}

.benefits-copy p:last-child {
  margin-bottom: 0;
}

.benefits-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 38px;
}

.benefits-actions--centered {
  justify-content: center;
}

.benefits-section--light {
  background: #111;
  color: #fff;
}

.benefits-section--dark {
  background: #050505;
}

.benefits-section--adventure {
  background: radial-gradient(circle at 82% 15%, #33110f 0, #090909 48%);
  border-bottom: 1px solid #2c2c2c;
  border-top: 1px solid #2c2c2c;
}

.benefits-section--benefits {
  background: #050505;
  color: #fff;
}

.benefits-section--life {
  background: #111;
}

.benefits-section--closing {
  background: radial-gradient(circle at 50% 15%, #28100e 0, #090909 58%);
}

.benefits-narrative {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 64px;
  align-items: start;
}

.benefits-narrative--wide {
  grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
  align-items: center;
}

.benefits-photo-card,
.life-gallery__item {
  margin: 0;
}

.benefits-photo-card {
  background: #141414;
  border: 1px solid #383838;
  padding: 10px;
}

.benefits-photo-card figcaption {
  color: #aaa;
  font-family: "Oswald", sans-serif;
  padding: 16px 18px;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.benefits-photo-card__image,
.benefits-feature-image,
.life-gallery__image {
  background: #181818;
}

.benefits-feature-image {
  border: 10px solid #111;
  outline: 1px solid #383838;
}

.benefits-section__intro {
  max-width: 780px;
  margin-bottom: 52px;
}

.benefits-section__intro--centered {
  margin-right: auto;
  margin-left: auto;
  text-align: center;
}

.benefits-section__intro--centered .benefits-copy {
  margin: 0 auto;
}

.benefit-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.benefit-card {
  background: #141414;
  border: 1px solid #303030;
  min-height: 290px;
  padding: 34px;
}

.benefit-card__label {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: .82rem;
  font-weight: 900;
  letter-spacing: .1em;
  margin: 0 0 12px;
  text-transform: uppercase;
}

.benefit-card__title {
  color: #fff;
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0 0 24px;
}

.benefit-card__list {
  color: #bdbdbd;
  line-height: 1.62;
  margin: 0;
  padding-left: 22px;
}

.benefit-card__list li + li {
  margin-top: 8px;
}

.life-gallery {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.life-gallery__support {
  color: #c7c7c7;
  font-size: 1.05rem;
  margin: 28px 0 0;
  text-align: center;
}

.life-gallery__item {
  background: #090909;
  border: 1px solid #383838;
  color: #fff;
  overflow: hidden;
  text-decoration: none;
  transition: border-color .2s ease, transform .2s ease;
}

.life-gallery__item:hover,
.life-gallery__item:focus-visible {
  border-color: #e23232;
  transform: translateY(-4px);
}

.life-gallery__image::v-deep .v-image__image {
  transition: transform .25s ease;
}

.life-gallery__item:hover .life-gallery__image::v-deep .v-image__image,
.life-gallery__item:focus-visible .life-gallery__image::v-deep .v-image__image {
  transform: scale(1.025);
}

.life-gallery__caption {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 18px 18px;
}

.life-gallery__caption small {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: .72rem;
  font-weight: 900;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.life-gallery__caption strong {
  font-family: "Oswald", sans-serif;
  font-size: 1.25rem;
  line-height: 1.25;
}

.benefits-closing {
  max-width: 920px;
  text-align: center;
}

.benefits-closing .benefits-title,
.benefits-closing .benefits-copy {
  margin-right: auto;
  margin-left: auto;
}

.benefits-closing__line {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: 1rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  margin: 46px 0 0;
  text-transform: uppercase;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 959px) {
  .benefits-hero,
  .benefits-section {
    padding: 72px 0;
  }

  .benefits-hero {
    min-height: auto;
    padding-top: 72px;
  }

  .benefits-narrative,
  .benefits-narrative--wide {
    grid-template-columns: 1fr;
    gap: 42px;
  }
}

@media (max-width: 599px) {
  .benefits-hero,
  .benefits-section {
    padding: 56px 0;
  }

  .benefits-hero {
    padding-top: 58px;
  }

  .benefits-title--hero {
    font-size: clamp(2.35rem, 12vw, 3.35rem);
  }

  .benefit-grid,
  .life-gallery {
    grid-template-columns: 1fr;
  }

  .benefit-card {
    min-height: 0;
    padding: 26px 22px;
  }

  .benefits-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .benefits-actions :deep(.v-btn) {
    width: 100%;
  }
}
</style>
